import { ComparisonResult, Scenario, ScenarioSummary, ScheduleResult } from '../domain/types.js';
import { hasEvents } from '../domain/equivalent-rate.js';
import { FormState } from './form-utils.js';
import { formatCompactRub, formatMonthLabel, formatPercent, formatRub, formatTerm } from './format.js';
import { interestSharePercent, overpaymentRatioLabel, resultNoteParts, updateKpiBar } from './summary.js';

export type ResultInput = {
  summary: ScenarioSummary;
  schedule: ScheduleResult;
  scenario: Scenario;
  state: FormState;
  comparison?: ComparisonResult;
  /** The scenario in the form is the baseline itself (no comparison possible). */
  isBaseline: boolean;
};

const STALE_TEXT = 'Расчёт не обновлён: исправьте ошибки в форме';

function setText(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

/** Fills the result strip (`#result`): tiles, their sub-lines, the total line, the note and the KPI bar. */
export function renderResult(input: ResultInput): void {
  const { summary, schedule, scenario, state, comparison, isBaseline } = input;
  const first = schedule.months[0];
  const firstPayment = first?.scheduledPayment ?? 0;
  const firstOverpayment = first?.overpayment ?? 0;

  setText('term-label', `${state.termYears.toFixed(0)} лет · ${Math.round(state.termYears * 12)} месяцев`);

  setText('summary-monthly', formatRub(firstPayment));
  setText(
    'summary-monthly-sub',
    firstOverpayment > 0 ? `+ ${formatRub(firstOverpayment)} досрочно в первом месяце` : 'в первом месяце',
  );
  setText('summary-interest', formatRub(summary.totalInterest));
  setText('summary-interest-sub', `${interestSharePercent(summary.totalInterest, scenario.principal).toLocaleString('ru-RU')}\u00a0% от суммы кредита`);
  setText('summary-payoff', formatMonthLabel(summary.payoffDate));
  setText('summary-payoff-sub', `срок по расчёту ${formatTerm(summary.actualTermMonths)}`);
  setText('summary-max-payment', formatRub(summary.maxMonthlyPayment));
  setText('summary-total-paid', formatRub(summary.totalPaid));
  setText('balance-tag', overpaymentRatioLabel(summary.totalInterest, scenario.principal));

  // The equivalent rate: the same loan without any event that pays the same interest. A plain
  // loan is equivalent to its own rate, so the line only makes sense once an event is on.
  const equivalent = document.getElementById('result-equivalent');
  if (equivalent) {
    const shown = hasEvents(scenario) && summary.equivalentRateApr !== null;
    equivalent.hidden = !shown;
    if (shown) {
      setText('summary-equivalent-rate', formatPercent(summary.equivalentRateApr));
      setText('summary-equivalent-term', formatTerm(scenario.termMonths));
    }
  }

  const note = document.getElementById('result-note');
  if (note) {
    note.replaceChildren(...resultNoteParts(state, summary, comparison, isBaseline).map(part => {
      if (!part.tone) return document.createTextNode(part.text);
      const span = document.createElement('span');
      span.className = part.tone;
      span.textContent = part.text;
      return span;
    }));
  }

  // The bar has three cells on a phone-width row, so money is shown compact («10,2 млн ₽»).
  updateKpiBar({
    monthly: formatCompactRub(firstPayment),
    totalInterest: formatCompactRub(summary.totalInterest),
    termLabel: formatTerm(summary.actualTermMonths),
  });
}

/**
 * Validation errors keep the last valid numbers on screen; they are marked stale and a
 * banner says why they are not moving.
 */
export function setResultStale(stale: boolean): void {
  document.querySelectorAll('#result .summary-grid article').forEach(tile => tile.classList.toggle('stale', stale));
  document.getElementById('result')?.classList.toggle('is-stale', stale);
  const banner = document.getElementById('result-stale');
  if (banner) {
    banner.textContent = STALE_TEXT;
    banner.hidden = !stale;
  }
}
